import type { PermissionCode } from "../../app/generated/shell-permission-codes";
import type { MessageKey } from "../../app/locale-store";
import type { ShellIconName } from "../../app/shell-icons";
import type { OperationalIconName } from "../../ui/operational-icon";
import type { WorkspaceComponentId } from "../../workspace";

/**
 * Navigation personas are presentation only. They never grant authority: the
 * active scoped grant and, for appointed functions, the active incident-role
 * assignment remain authoritative on both the client and API.
 */
export const PERSPECTIVE_IDS = [
  "COMMAND",
  "LUK_INTAKE",
  "SITUATION",
  "ASSESSMENT",
  "OPERATIONS",
  "LOGISTICS",
  "READINESS",
  "FIELD_LEADERSHIP",
  "FIELD_PARTICIPANT",
  "DOCUMENTATION",
  "COMMUNICATIONS",
  "REGIONAL",
  "ADMIN",
] as const;

export type PerspectiveId = (typeof PERSPECTIVE_IDS)[number];
export type OperationalPerspectiveId = Exclude<PerspectiveId, "ADMIN">;

/**
 * Stable shell-workspace identities. Several header entries may target the
 * same workspace with a different focused panel or workbench screen; that is
 * intentional and lets them share one saved FlexLayout model.
 */
export const OPERATIONAL_WORKSPACE_IDS = [
  "HOME",
  "REQ",
  "ICC",
  "CAP",
  "DISPATCH",
  "OPS",
  "ORD",
  "SITU",
  "ETB",
  "COLLAB",
  "FIELD",
  "LOGI",
  "CLOSE",
  "ADMIN",
] as const;

export type OperationalWorkspaceId = (typeof OPERATIONAL_WORKSPACE_IDS)[number];
export type OperationsFlexWorkspaceId = Exclude<
  OperationalWorkspaceId,
  "HOME" | "ADMIN"
>;

export type IncidentAppointmentRole =
  | "INCIDENT_COMMANDER"
  | "S1_PERSONNEL"
  | "S2_SITUATION_REPORTING"
  | "S3_OPERATIONS"
  | "S4_LOGISTICS"
  | "S6_COMMUNICATIONS"
  | "ETB_KEEPER";

export interface PerspectiveTarget {
  readonly kind: "operations-panel" | "route";
  readonly path: string;
  readonly workspaceId?: OperationalWorkspaceId;
  readonly component?: WorkspaceComponentId;
  readonly screen?: string;
}

export interface HeaderWorkspaceEntryDefinition {
  readonly id: string;
  readonly workspaceId: OperationalWorkspaceId;
  readonly icon: ShellIconName;
  readonly operationalIcon?: OperationalIconName;
  readonly labelKey: MessageKey;
  /** Protected view whose DB assignment and feature policy discover this entry. */
  readonly gateFeatureId: string;
  /** Additional discovery requirement; never an action-authorization shortcut. */
  readonly requiredPermissions: readonly PermissionCode[];
  readonly target: PerspectiveTarget;
}

export interface PerspectiveDefinition {
  readonly id: PerspectiveId;
  readonly icon: ShellIconName;
  readonly label: Readonly<Record<"de" | "en", string>>;
  /** Shell feature ids allowed in the compact primary header for this persona. */
  readonly headerFeatureIds: readonly string[];
  /** Ordered desktop/tablet workspace entries; the shell shows five plus More. */
  readonly headerWorkspaceEntryIds: readonly string[];
  /**
   * Optional strict module-catalogue allowlist for a role-focused workspace.
   * Omitted perspectives retain the normal permission-filtered catalogue.
   */
  readonly moduleFeatureIds?: readonly string[];
  readonly defaultTarget: PerspectiveTarget;
  /** Collapsed utility panels kept beside, rather than inside, the workbench. */
  readonly contextComponents: readonly WorkspaceComponentId[];
  readonly primaryComponents: readonly WorkspaceComponentId[];
  readonly supportComponents: readonly WorkspaceComponentId[];
  /** Authorized, persona-relevant panels that may be restored/opened on demand. */
  readonly optionalComponents: readonly WorkspaceComponentId[];
  readonly mobileComponents: readonly WorkspaceComponentId[];
  readonly includeRegionalCoordination: boolean;
}

export interface RolePerspectiveAssignment {
  readonly roleCode: string;
  readonly label: Readonly<Record<"de" | "en", string>>;
  readonly perspective: PerspectiveId | null;
  /** Layout used when this context explicitly enters /operations. */
  readonly operationsPerspective: OperationalPerspectiveId | null;
  readonly defaultTarget: PerspectiveTarget | null;
  readonly requiresMissionRead: boolean;
  /** Discovery precondition only. Component actions remain independently gated. */
  readonly requiredPermissions: readonly PermissionCode[];
  readonly defaultComponents: readonly WorkspaceComponentId[];
  readonly optionalComponents: readonly WorkspaceComponentId[];
  readonly supportedDevices: readonly ("desktop" | "tablet" | "mobile")[];
  readonly capabilityMode: "NONE" | "READ" | "READ_WRITE";
  readonly scope: Readonly<{
    organization: "ACTIVE_GRANT";
    incident: "ACTIVE_APPOINTMENT_OR_GRANT" | "NONE";
    mission: "ACTIVE_GRANT" | "NONE";
  }>;
  readonly expirationBehavior: "RECOMPUTE_ON_CONTEXT_REFRESH";
  readonly incidentAppointmentRole?: IncidentAppointmentRole;
  /** Role-specific desktop/tablet order when a shared perspective is too broad. */
  readonly headerWorkspaceEntryIds?: readonly string[];
  /**
   * Hard allowlist of primary-header item ids for this ROLE, applied after the
   * perspective's own header selection. Present only for single-purpose roles
   * whose header should carry their one workspace and nothing else — the same
   * intent as the `technical_adviser` single-item header, but declared per role
   * so it never narrows the other roles sharing the same perspective.
   * Omitted (the normal case) leaves the header exactly as the perspective and
   * permission filters produced it.
   */
  readonly headerItemIds?: readonly string[];
}

const humanizeRoleCode = (roleCode: string): string => roleCode
  .split("_")
  .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
  .join(" ");

const target = (
  path: string,
  component?: WorkspaceComponentId,
  screen?: string,
  workspaceId?: OperationalWorkspaceId,
): PerspectiveTarget => component === undefined
  ? {
      kind: "route",
      path,
      ...(screen === undefined ? {} : { screen }),
      ...(workspaceId === undefined ? {} : { workspaceId }),
    }
  : {
      kind: "operations-panel",
      path,
      component,
      ...(screen === undefined ? {} : { screen }),
      ...(workspaceId === undefined ? {} : { workspaceId }),
    };

const entry = (
  id: string,
  workspaceId: OperationalWorkspaceId,
  labelKey: MessageKey,
  icon: ShellIconName,
  gateFeatureId: string,
  requiredPermissions: readonly PermissionCode[],
  destination: PerspectiveTarget,
  operationalIcon?: OperationalIconName,
): HeaderWorkspaceEntryDefinition => ({
  id,
  workspaceId,
  labelKey,
  icon,
  gateFeatureId,
  requiredPermissions,
  target: destination,
  ...(operationalIcon === undefined ? {} : { operationalIcon }),
});

/**
 * The closed set of desktop/tablet workspace shortcuts. These are
 * presentation definitions only: the protected-view gate, focused panel gate
 * and every server command still authorize independently.
 */
const HEADER_WORKSPACE_ENTRY_LIST: readonly HeaderWorkspaceEntryDefinition[] = [
  entry(
    "intake",
    "REQ",
    "workspaceIntake",
    "requests",
    "incident-intake",
    ["assistance_request_read"],
    target("/modules/incident-intake"),
  ),
  entry(
    "incident-setup",
    "ICC",
    "workspaceIncidentSetup",
    "operations",
    "operations",
    // Opening the workspace is read/discovery authority. Each contained action
    // (incident creation, role mutation, LuK staffing) keeps its own permission.
    ["mission_read"],
    // Incident setup is the native incident workspace. Keeping this as a
    // route target avoids reopening the retired Operations mission-board and
    // gives Incident information, LuK duty rostering and role assignment one
    // shared incident context.
    target("/?open=incidents"),
  ),
  entry(
    "requests-assessment",
    "REQ",
    "workspaceRequestsAssessment",
    "requests",
    "incident-intake",
    ["requirement_read"],
    target("/requirements"),
    "requirements",
  ),
  entry(
    "capabilities-resources",
    "CAP",
    "workspaceCapabilitiesResources",
    "resources",
    "resource-requests",
    ["mission_read"],
    target("/operations", "feature-capability-requirements", undefined, "CAP"),
    "capability-resources",
  ),
  entry(
    "coordination",
    "ICC",
    "workspaceCoordination",
    "command",
    "operations",
    ["mission_read"],
    target("/operations", "feature-regional-coordination", undefined, "ICC"),
  ),
  entry(
    "dispatch-mobilization",
    "DISPATCH",
    "workspaceDispatchMobilization",
    "resources",
    "resource-requests",
    ["mission_read"],
    target("/operations", "deployment-roster", undefined, "DISPATCH"),
  ),
  entry(
    "collaboration",
    "COLLAB",
    "workspaceCollaboration",
    "collaboration",
    "collaboration",
    ["channel_read"],
    target("/operations", "collaboration", undefined, "COLLAB"),
  ),
  entry(
    "situation",
    "SITU",
    "workspaceSituation",
    "situation",
    "lagekarte",
    ["mission_read"],
    target("/operations", "situation-map", undefined, "SITU"),
  ),
  entry(
    "operational-logbook",
    "ETB",
    "workspaceOperationalLogbook",
    "book",
    "operational-log",
    ["mission_read"],
    target("/operations", "etb-log", undefined, "ETB"),
  ),
  entry(
    "mission-orders",
    "ORD",
    "workspaceMissionOrders",
    "orders",
    "orders",
    ["mission_read"],
    target("/operations", "mission-orders", undefined, "ORD"),
  ),
  entry(
    "mobilization",
    "FIELD",
    "workspaceMobilization",
    "registration",
    "operations",
    ["mission_read"],
    target("/operations", "mobilization", undefined, "FIELD"),
  ),
  entry(
    "mission-orders-compose",
    "ORD",
    "workspaceMissionOrders",
    "orders",
    "orders",
    ["order_create"],
    target("/operations", "mission-orders", "compose", "ORD"),
  ),
  entry(
    "logistics",
    "LOGI",
    "workspaceLogistics",
    "logistics",
    "logistics",
    ["mission_read"],
    target("/logistics", "logistics", undefined, "LOGI"),
  ),
  entry(
    "completion",
    "CLOSE",
    "workspaceCompletion",
    "operations",
    "operations",
    ["mission_read"],
    target("/operations", "incident-completion", undefined, "CLOSE"),
  ),
  entry(
    "technical-assessment",
    "SITU",
    "workspaceTechnicalAssessment",
    "adviser",
    "adviser-field",
    ["adviser_assignment_read"],
    target("/modules/adviser-field", undefined, undefined, "SITU"),
  ),
  entry(
    "command",
    "ICC",
    "workspaceCommand",
    "command",
    "operations",
    ["command_structure_read"],
    target("/operations", "command-structure", undefined, "ICC"),
  ),
  entry(
    "forms",
    "ETB",
    "workspaceForms",
    "forms",
    "forms",
    ["form_read"],
    target("/operations", "incident-reporting", undefined, "ETB"),
  ),
  entry(
    "personnel-forces",
    "CAP",
    "workspacePersonnelForces",
    "resources",
    "personnel",
    ["person_read"],
    target("/operations", "personnel-directory", undefined, "CAP"),
  ),
  entry(
    "readiness",
    "CAP",
    "workspaceReadiness",
    "readiness",
    "readiness",
    ["readiness_read"],
    target("/operations", "readiness-status", undefined, "CAP"),
  ),
  entry(
    "force-registration",
    "CAP",
    "workspaceForceRegistration",
    "registration",
    "registration",
    ["registration_overview_read"],
    target("/operations", "mobilization", undefined, "CAP"),
  ),
  entry(
    "situation-reports",
    "SITU",
    "workspaceSituationReports",
    "situation",
    "lagekarte",
    ["situation_report_read"],
    target("/operations", "lage-board", undefined, "SITU"),
  ),
  entry(
    "safety-checks",
    "SITU",
    "workspaceSafetyChecks",
    "readiness",
    "lagekarte",
    ["recurring_check_read"],
    target("/operations", "safety-checks", undefined, "SITU"),
  ),
  entry(
    "closure-reports",
    "ETB",
    "workspaceClosureReports",
    "forms",
    "operational-log",
    ["report_view"],
    target("/operations", "reporting", undefined, "ETB"),
  ),
  entry(
    "operations",
    "OPS",
    "workspaceOperations",
    "operations",
    "operations",
    ["mission_read"],
    target("/operations", "field-tasks", undefined, "OPS"),
  ),
  entry(
    "logistics-movement",
    "LOGI",
    "workspaceMovement",
    "registration",
    "field-operations",
    ["registration_movement_record"],
    target("/logistics", "mobilization", undefined, "LOGI"),
  ),
  entry(
    "field-movement",
    "FIELD",
    "workspaceMovement",
    "registration",
    "field-operations",
    ["registration_movement_record"],
    target("/operations", "mobilization", undefined, "FIELD"),
  ),
  entry(
    "assets-readiness",
    "LOGI",
    "workspaceAssetsReadiness",
    "logistics",
    "assets",
    ["asset_read"],
    target("/logistics", "asset-directory", undefined, "LOGI"),
  ),
  entry(
    "assets",
    "LOGI",
    "workspaceAssets",
    "logistics",
    "assets",
    ["asset_read"],
    target("/logistics", "asset-directory", undefined, "LOGI"),
  ),
  entry(
    "resource-requests",
    "REQ",
    "workspaceResourceRequests",
    "requests",
    "resource-requests",
    ["mission_read"],
    target("/operations", "resource-requests", undefined, "REQ"),
  ),
  entry(
    "command-structure",
    "COLLAB",
    "workspaceCommandStructure",
    "command",
    "operations",
    ["command_structure_read"],
    target("/operations", "command-structure", undefined, "COLLAB"),
  ),
  entry(
    "field-execution",
    "FIELD",
    "workspaceFieldExecution",
    "tactical",
    "field-operations",
    ["mission_read"],
    target("/operations", "field-tasks", undefined, "FIELD"),
  ),
  entry(
    "regional-coordination",
    "ICC",
    "workspaceRegionalCoordination",
    "command",
    "operations",
    ["mission_read"],
    target("/operations", "feature-regional-coordination", undefined, "ICC"),
  ),
  entry(
    "assistance-requests",
    "REQ",
    "workspaceAssistanceRequests",
    "requests",
    "incident-intake",
    ["assistance_request_read"],
    target("/operations", "resource-requests", undefined, "REQ"),
  ),
  entry(
    "regional-resources",
    "CAP",
    "workspaceRegionalResources",
    "resources",
    "resource-requests",
    ["mission_read"],
    target("/operations", "feature-regional-coordination", undefined, "ICC"),
  ),
  entry(
    "administration",
    "ADMIN",
    "workspaceAdministration",
    "administration",
    "administration",
    [],
    target("/administration", undefined, undefined, "ADMIN"),
  ),
];

export const HEADER_WORKSPACE_ENTRIES: ReadonlyMap<
  string,
  HeaderWorkspaceEntryDefinition
> = new Map(
  HEADER_WORKSPACE_ENTRY_LIST.map((value) => [value.id, value] as const),
);

export interface OperationalWorkspaceDefinition {
  readonly id: OperationsFlexWorkspaceId;
  readonly labelKey: MessageKey;
  readonly primaryComponents: readonly WorkspaceComponentId[];
  readonly supportComponents: readonly WorkspaceComponentId[];
  readonly optionalComponents: readonly WorkspaceComponentId[];
}

const workspace = (
  id: OperationsFlexWorkspaceId,
  labelKey: MessageKey,
  primaryComponents: readonly WorkspaceComponentId[],
  supportComponents: readonly WorkspaceComponentId[],
  optionalComponents: readonly WorkspaceComponentId[],
): OperationalWorkspaceDefinition => ({
  id,
  labelKey,
  primaryComponents,
  supportComponents,
  optionalComponents,
});

export const CAPABILITY_WORKSPACE_PANEL_IDS = [
  "feature-capability-requirements",
  "feature-capability-coverage",
  "feature-capability-candidates",
  "feature-capability-team",
  "feature-capability-selected-unit",
] as const satisfies readonly WorkspaceComponentId[];

export const DISPATCH_ALERTING_PANEL_IDS = [
  "dispatch-alerting",
  "alert-responses",
] as const satisfies readonly WorkspaceComponentId[];

function isCapabilityWorkspacePanel(
  component: WorkspaceComponentId,
): component is typeof CAPABILITY_WORKSPACE_PANEL_IDS[number] {
  return (CAPABILITY_WORKSPACE_PANEL_IDS as readonly WorkspaceComponentId[]).includes(component);
}

/**
 * Panels split out of `situation-map` when the Situation workspace stopped
 * stacking the map, the review register and the object editor into one
 * scrolling panel. They are relevant to exactly the perspectives the map
 * itself is relevant to — they are the same feature, docked separately.
 */
export const SITUATION_MAP_PANEL_IDS = [
  "situation-objects",
  "situation-object-editor",
] as const satisfies readonly WorkspaceComponentId[];

function isSituationMapPanel(
  component: WorkspaceComponentId,
): component is typeof SITUATION_MAP_PANEL_IDS[number] {
  return (SITUATION_MAP_PANEL_IDS as readonly WorkspaceComponentId[]).includes(component);
}

function isDispatchAlertingPanel(
  component: WorkspaceComponentId,
): component is typeof DISPATCH_ALERTING_PANEL_IDS[number] {
  return (DISPATCH_ALERTING_PANEL_IDS as readonly WorkspaceComponentId[]).includes(component);
}

export const OPERATIONAL_WORKSPACES: Readonly<Record<
  OperationsFlexWorkspaceId,
  OperationalWorkspaceDefinition
>> = {
  REQ: workspace(
    "REQ",
    "workspaceRequestsAssessment",
    ["requirement-review", "resource-requests"],
    ["deployment-planning", "incident-reporting"],
    ["etb-log"],
  ),
  ICC: workspace(
    "ICC",
    "workspaceCommandCoordination",
    ["command-structure", "feature-regional-coordination", "feature-regional-support-inbox"],
    [],
    ["etb-log", "reporting"],
  ),
  CAP: workspace(
    "CAP",
    "workspaceCapabilitiesResources",
    CAPABILITY_WORKSPACE_PANEL_IDS,
    ["personnel-directory", "asset-directory", "readiness-status"],
    ["deployment-planning", "mobilization", "logistics"],
  ),
  DISPATCH: workspace(
    "DISPATCH",
    "workspaceDispatchMobilization",
    ["deployment-roster"],
    DISPATCH_ALERTING_PANEL_IDS,
    ["commitment-allocation", "resource-requests", "readiness-status"],
  ),
  OPS: workspace(
    "OPS",
    "workspaceOperations",
    ["workflow-board", "field-tasks"],
    ["mission-orders", "situation-map"],
    ["collaboration", "safety-checks"],
  ),
  ORD: workspace(
    "ORD",
    "workspaceMissionOrders",
    ["mission-orders"],
    ["order-execution-status"],
    ["legacy-mission-orders", "field-tasks", "incident-reporting"],
  ),
  SITU: workspace(
    "SITU",
    "workspaceSituationEvidence",
    ["situation-map"],
    ["situation-objects", "situation-object-editor", "lage-board", "safety-checks", "reporting"],
    ["map-legend", "incident-reporting", "collaboration"],
  ),
  ETB: workspace(
    "ETB",
    "workspaceOperationalLogbook",
    ["etb-log"],
    ["incident-reporting", "reporting"],
    ["situation-map"],
  ),
  COLLAB: workspace(
    "COLLAB",
    "workspaceCollaboration",
    ["collaboration"],
    ["command-structure"],
    ["etb-log", "feature-regional-coordination"],
  ),
  FIELD: workspace(
    "FIELD",
    "workspaceFieldExecution",
    ["field-tasks", "mobilization"],
    ["situation-map", "readiness-status"],
    ["logistics", "safety-checks", "collaboration", "incident-reporting"],
  ),
  LOGI: workspace(
    "LOGI",
    "workspaceLogistics",
    ["logistics", "asset-directory"],
    ["deployment-asset-status", "resource-requests", "readiness-status"],
    ["personnel-directory", "reporting", "incident-reporting", "mobilization"],
  ),
  CLOSE: workspace(
    "CLOSE",
    "workspaceCompletionClosure",
    ["incident-completion"],
    [],
    [
      "etb-log",
      "incident-reporting",
      "reporting",
      "lage-board",
      "situation-map",
      "mission-orders",
      "deployment-planning",
      "resource-requests",
      "logistics",
      "mobilization",
      "readiness-status",
      "personnel-directory",
      "collaboration",
      "command-structure",
    ],
  ),
};

const ALL_OPERATIONAL_OPTIONALS: readonly WorkspaceComponentId[] = [
  "workflow-board",
  "incident-completion",
  "situation-map",
  "alerting",
  "safety-checks",
  "requirement-review",
  "deployment-planning",
  "commitment-allocation",
  "deployment-roster",
  "resource-requests",
  "mission-orders",
  "legacy-mission-orders",
  "mobilization",
  "order-execution-status",
  "deployment-asset-status",
  "field-tasks",
  "lage-board",
  "logistics",
  "collaboration",
  "etb-log",
  "incident-reporting",
  "reporting",
  "command-structure",
  "personnel-directory",
  "asset-directory",
  "readiness-status",
] as const;

/**
 * The operational rail is part of the role composition contract, not shell
 * decoration. Every operational perspective receives the same two context
 * entry points in the same order. Coordination is workspace content, not a
 * third rail selector, so every workspace keeps the same compact context rail.
 */
const BASE_OPERATIONAL_CONTEXT: readonly WorkspaceComponentId[] = [
  "feature-active-missions",
  "feature-incident-context",
] as const;

const COORDINATING_OPERATIONAL_CONTEXT: readonly WorkspaceComponentId[] = [
  ...BASE_OPERATIONAL_CONTEXT,
] as const;

export const PERSPECTIVES: Readonly<Record<PerspectiveId, PerspectiveDefinition>> = {
  COMMAND: {
    id: "COMMAND",
    icon: "command",
    label: { de: "Führung", en: "Command" },
    headerFeatureIds: ["operations", "resource-requests", "lagekarte", "collaboration", "operational-log", "forms"],
    headerWorkspaceEntryIds: ["command", "mission-orders", "mobilization", "situation", "operational-logbook", "collaboration", "completion", "capabilities-resources", "forms"],
    defaultTarget: target("/operations", "command-structure", undefined, "ICC"),
    contextComponents: COORDINATING_OPERATIONAL_CONTEXT,
    primaryComponents: ["resource-requests", "deployment-planning", "commitment-allocation", "mission-orders", "order-execution-status", "mobilization", "field-tasks"],
    supportComponents: ["safety-checks", "lage-board"],
    optionalComponents: [...ALL_OPERATIONAL_OPTIONALS, "feature-regional-coordination"],
    mobileComponents: ["workflow-board", "incident-completion", "situation-map", "mission-orders", "mobilization", "field-tasks", "collaboration", "incident-reporting", "etb-log"],
    includeRegionalCoordination: true,
  },
  LUK_INTAKE: {
    id: "LUK_INTAKE",
    icon: "requests",
    label: { de: "Anforderungen", en: "Requests" },
    headerFeatureIds: ["incident-intake", "operations", "resource-requests", "lagekarte", "collaboration", "forms"],
    headerWorkspaceEntryIds: ["intake", "incident-setup", "requests-assessment", "capabilities-resources", "dispatch-mobilization", "mobilization", "coordination", "collaboration", "situation", "operational-logbook", "mission-orders", "logistics", "completion"],
    defaultTarget: target("/modules/incident-intake"),
    contextComponents: COORDINATING_OPERATIONAL_CONTEXT,
    primaryComponents: ["alerting", "resource-requests", "deployment-planning", "commitment-allocation", "deployment-roster"],
    supportComponents: ["collaboration"],
    optionalComponents: ["incident-completion", "requirement-review", "situation-map", "incident-reporting", "etb-log", "mission-orders", "order-execution-status", "logistics", "feature-regional-coordination", "feature-regional-support-inbox"],
    mobileComponents: ["workflow-board", "incident-completion", "alerting", "resource-requests", "deployment-planning", "commitment-allocation", "mobilization", "situation-map", "collaboration", "incident-reporting"],
    includeRegionalCoordination: true,
  },
  SITUATION: {
    id: "SITUATION",
    icon: "situation",
    label: { de: "Lage", en: "Situation" },
    headerFeatureIds: ["situation-cop"],
    headerWorkspaceEntryIds: ["situation", "situation-reports", "operational-logbook", "collaboration", "forms", "safety-checks", "closure-reports"],
    moduleFeatureIds: ["situation-cop"],
    defaultTarget: target("/modules/situation-cop"),
    contextComponents: BASE_OPERATIONAL_CONTEXT,
    primaryComponents: ["situation-map", "incident-reporting", "lage-board"],
    supportComponents: ["safety-checks", "reporting"],
    optionalComponents: ["incident-completion", "alerting", "etb-log", "collaboration", "command-structure", "mobilization", "logistics"],
    mobileComponents: ["workflow-board", "incident-completion", "situation-map", "lage-board", "safety-checks", "collaboration", "incident-reporting", "etb-log"],
    includeRegionalCoordination: false,
  },
  ASSESSMENT: {
    id: "ASSESSMENT",
    icon: "adviser",
    label: { de: "Fachberatung", en: "Assessment" },
    headerFeatureIds: ["adviser-field"],
    headerWorkspaceEntryIds: ["technical-assessment"],
    moduleFeatureIds: ["adviser-field"],
    defaultTarget: target("/modules/adviser-field"),
    contextComponents: BASE_OPERATIONAL_CONTEXT,
    primaryComponents: ["requirement-review", "safety-checks"],
    supportComponents: ["lage-board", "collaboration"],
    optionalComponents: ["incident-completion", "situation-map", "incident-reporting", "etb-log", "deployment-planning", "resource-requests"],
    mobileComponents: ["workflow-board", "incident-completion", "requirement-review", "safety-checks", "situation-map", "collaboration", "incident-reporting"],
    includeRegionalCoordination: false,
  },
  OPERATIONS: {
    id: "OPERATIONS",
    icon: "operations",
    label: { de: "Einsatzplanung", en: "Operations" },
    headerFeatureIds: ["operations", "resource-requests", "lagekarte", "collaboration", "forms"],
    headerWorkspaceEntryIds: ["operations", "mission-orders", "situation", "operational-logbook", "collaboration", "capabilities-resources", "logistics", "completion"],
    defaultTarget: target("/operations", "field-tasks", undefined, "OPS"),
    contextComponents: BASE_OPERATIONAL_CONTEXT,
    primaryComponents: ["resource-requests", "deployment-planning", "commitment-allocation", "mission-orders", "order-execution-status", "field-tasks"],
    supportComponents: [],
    optionalComponents: ["incident-completion", "legacy-mission-orders", "situation-map", "safety-checks", "logistics", "personnel-directory", "asset-directory", "collaboration", "etb-log", "command-structure", "readiness-status", "incident-reporting", "reporting"],
    mobileComponents: ["workflow-board", "incident-completion", "deployment-planning", "resource-requests", "mission-orders", "mobilization", "field-tasks", "situation-map", "collaboration"],
    includeRegionalCoordination: false,
  },
  LOGISTICS: {
    id: "LOGISTICS",
    icon: "logistics",
    label: { de: "Logistik", en: "Logistics" },
    // Readiness and registration (mobilization) join the header because they
    // are the quartermaster's own recurring work — attesting equipment
    // readiness and recording vehicle/equipment condition on movement — not
    // something reached by hunting through the Operations workspace. Nothing
    // is removed: `operations` stays, so the full operational workspace is
    // still one click away for anyone holding this perspective.
    headerFeatureIds: ["logistics", "resource-requests", "readiness", "registration", "operations", "collaboration", "forms"],
    headerWorkspaceEntryIds: ["logistics", "capabilities-resources", "logistics-movement", "operational-logbook", "collaboration", "assets", "forms", "situation"],
    defaultTarget: target("/logistics", "logistics", undefined, "LOGI"),
    contextComponents: BASE_OPERATIONAL_CONTEXT,
    primaryComponents: ["logistics", "resource-requests", "deployment-asset-status"],
    supportComponents: [],
    optionalComponents: ["incident-completion", "deployment-planning", "asset-directory", "readiness-status", "personnel-directory", "collaboration", "etb-log", "reporting", "situation-map", "field-tasks", "incident-reporting", "safety-checks", "mobilization"],
    mobileComponents: ["workflow-board", "incident-completion", "logistics", "resource-requests", "asset-directory", "readiness-status", "mobilization", "collaboration"],
    includeRegionalCoordination: false,
  },
  READINESS: {
    id: "READINESS",
    icon: "readiness",
    label: { de: "Bereitschaft", en: "Readiness" },
    headerFeatureIds: ["readiness", "registration", "operations", "forms", "collaboration"],
    headerWorkspaceEntryIds: ["personnel-forces", "readiness", "force-registration", "operational-logbook", "collaboration", "forms", "situation"],
    defaultTarget: target("/operations", "readiness-status", undefined, "CAP"),
    contextComponents: BASE_OPERATIONAL_CONTEXT,
    primaryComponents: ["personnel-directory", "readiness-status", "mobilization", "asset-directory"],
    supportComponents: [],
    optionalComponents: ["incident-completion", "deployment-planning", "collaboration", "etb-log", "command-structure", "logistics", "safety-checks", "incident-reporting", "situation-map"],
    mobileComponents: ["workflow-board", "incident-completion", "readiness-status", "personnel-directory", "mobilization", "asset-directory", "collaboration", "incident-reporting"],
    includeRegionalCoordination: false,
  },
  FIELD_LEADERSHIP: {
    id: "FIELD_LEADERSHIP",
    icon: "orders",
    label: { de: "Einheitsführung", en: "Field leadership" },
    headerFeatureIds: ["field-operations", "lagekarte", "collaboration", "forms", "readiness"],
    headerWorkspaceEntryIds: ["field-execution", "situation", "collaboration", "field-movement", "readiness", "logistics", "closure-reports"],
    defaultTarget: target("/modules/field-operations"),
    contextComponents: BASE_OPERATIONAL_CONTEXT,
    primaryComponents: ["mission-orders", "mobilization", "field-tasks", "readiness-status", "logistics"],
    supportComponents: [],
    optionalComponents: ["situation-map", "safety-checks", "collaboration", "incident-reporting", "resource-requests", "deployment-planning", "etb-log", "reporting"],
    mobileComponents: ["workflow-board", "mission-orders", "mobilization", "field-tasks", "situation-map", "safety-checks", "collaboration", "incident-reporting"],
    includeRegionalCoordination: false,
  },
  FIELD_PARTICIPANT: {
    id: "FIELD_PARTICIPANT",
    icon: "tactical",
    label: { de: "Einsatzkraft", en: "Field participant" },
    headerFeatureIds: ["field-operations", "lagekarte", "collaboration", "forms"],
    headerWorkspaceEntryIds: ["field-execution"],
    defaultTarget: target("/modules/field-operations"),
    contextComponents: BASE_OPERATIONAL_CONTEXT,
    primaryComponents: ["field-tasks", "mobilization", "safety-checks", "collaboration"],
    supportComponents: [],
    optionalComponents: ["workflow-board", "field-tasks", "situation-map", "mobilization", "safety-checks", "collaboration", "incident-reporting", "etb-log"],
    mobileComponents: ["workflow-board", "field-tasks", "situation-map", "safety-checks", "collaboration", "incident-reporting"],
    includeRegionalCoordination: false,
  },
  DOCUMENTATION: {
    id: "DOCUMENTATION",
    icon: "book",
    label: { de: "Dokumentation", en: "Documentation" },
    headerFeatureIds: ["operational-log", "forms", "lagekarte", "collaboration", "operations"],
    headerWorkspaceEntryIds: ["operational-logbook", "forms", "collaboration", "closure-reports", "situation", "completion"],
    defaultTarget: target("/operations", "etb-log", undefined, "ETB"),
    contextComponents: BASE_OPERATIONAL_CONTEXT,
    primaryComponents: ["etb-log", "incident-reporting", "lage-board", "reporting"],
    supportComponents: ["collaboration"],
    optionalComponents: ["incident-completion", "situation-map", "safety-checks", "command-structure", "mission-orders", "mobilization", "logistics"],
    mobileComponents: ["workflow-board", "incident-completion", "etb-log", "incident-reporting", "lage-board", "reporting", "situation-map", "collaboration"],
    includeRegionalCoordination: false,
  },
  COMMUNICATIONS: {
    id: "COMMUNICATIONS",
    icon: "collaboration",
    label: { de: "IuK", en: "Communications" },
    headerFeatureIds: ["collaboration", "lagekarte", "operational-log", "operations", "forms"],
    headerWorkspaceEntryIds: ["collaboration", "forms", "operational-logbook", "situation", "command-structure"],
    defaultTarget: target("/operations", "collaboration", undefined, "COLLAB"),
    contextComponents: BASE_OPERATIONAL_CONTEXT,
    primaryComponents: ["collaboration", "command-structure", "alerting"],
    supportComponents: [],
    optionalComponents: ["incident-completion", "situation-map", "incident-reporting", "etb-log", "safety-checks", "lage-board"],
    mobileComponents: ["workflow-board", "incident-completion", "collaboration", "command-structure", "situation-map", "incident-reporting", "etb-log"],
    includeRegionalCoordination: false,
  },
  REGIONAL: {
    id: "REGIONAL",
    icon: "resources",
    label: { de: "Regionalkoordination", en: "Regional coordination" },
    headerFeatureIds: ["incident-intake", "operations", "resource-requests", "lagekarte", "collaboration"],
    headerWorkspaceEntryIds: ["regional-coordination", "assistance-requests", "regional-resources", "situation", "collaboration", "logistics", "closure-reports", "operational-logbook"],
    defaultTarget: target("/operations", "feature-regional-coordination", undefined, "ICC"),
    contextComponents: COORDINATING_OPERATIONAL_CONTEXT,
    primaryComponents: ["resource-requests", "deployment-planning", "logistics", "reporting", "feature-regional-coordination"],
    supportComponents: ["lage-board"],
    optionalComponents: ["incident-completion", "situation-map", "etb-log", "readiness-status", "personnel-directory", "asset-directory", "incident-reporting"],
    mobileComponents: ["workflow-board", "incident-completion", "resource-requests", "deployment-planning", "situation-map", "logistics", "collaboration", "reporting"],
    includeRegionalCoordination: true,
  },
  ADMIN: {
    id: "ADMIN",
    icon: "administration",
    label: { de: "Administration", en: "Administration" },
    headerFeatureIds: ["administration"],
    headerWorkspaceEntryIds: ["administration"],
    defaultTarget: target("/administration"),
    contextComponents: [],
    primaryComponents: [],
    supportComponents: [],
    optionalComponents: [],
    mobileComponents: [],
    includeRegionalCoordination: false,
  },
};

const DEFAULT_WORKSPACE_BY_PERSPECTIVE: Readonly<Record<
  OperationalPerspectiveId,
  OperationsFlexWorkspaceId
>> = {
  ASSESSMENT: "SITU",
  COMMAND: "ICC",
  COMMUNICATIONS: "COLLAB",
  DOCUMENTATION: "ETB",
  FIELD_LEADERSHIP: "FIELD",
  FIELD_PARTICIPANT: "FIELD",
  LOGISTICS: "LOGI",
  LUK_INTAKE: "REQ",
  OPERATIONS: "OPS",
  READINESS: "CAP",
  REGIONAL: "ICC",
  SITUATION: "SITU",
};

const WORKSPACE_BY_PANEL: Readonly<Partial<Record<
  WorkspaceComponentId,
  OperationsFlexWorkspaceId
>>> = {
  "asset-directory": "LOGI",
  alerting: "ICC",
  collaboration: "COLLAB",
  "command-structure": "ICC",
  "commitment-allocation": "DISPATCH",
  "deployment-roster": "DISPATCH",
  "deployment-asset-status": "LOGI",
  "deployment-planning": "CAP",
  "feature-capability-requirements": "CAP",
  "feature-capability-coverage": "CAP",
  "feature-capability-candidates": "CAP",
  "feature-capability-team": "CAP",
  "feature-capability-selected-unit": "CAP",
  "etb-log": "ETB",
  "feature-regional-coordination": "ICC",
  "feature-regional-support-inbox": "ICC",
  "field-tasks": "OPS",
  "incident-completion": "CLOSE",
  "incident-reporting": "ETB",
  "lage-board": "SITU",
  "legacy-mission-orders": "ORD",
  logistics: "LOGI",
  "map-legend": "SITU",
  "mission-board": "ICC",
  "mission-orders": "ORD",
  "order-execution-status": "ORD",
  "personnel-directory": "CAP",
  "readiness-status": "CAP",
  reporting: "ETB",
  "requirement-review": "REQ",
  "resource-requests": "REQ",
  "safety-checks": "SITU",
  "situation-map": "SITU",
  "situation-objects": "SITU",
  "situation-object-editor": "SITU",
  "workflow-board": "OPS",
};

const CLOSE_SUPPORT_BY_PERSPECTIVE: Readonly<Record<
  OperationalPerspectiveId,
  readonly WorkspaceComponentId[]
>> = {
  ASSESSMENT: ["situation-map", "safety-checks"],
  COMMAND: ["etb-log", "reporting", "lage-board"],
  COMMUNICATIONS: ["collaboration", "command-structure", "etb-log"],
  DOCUMENTATION: ["etb-log", "incident-reporting", "reporting"],
  FIELD_LEADERSHIP: ["mobilization", "readiness-status", "situation-map"],
  FIELD_PARTICIPANT: ["mobilization", "situation-map"],
  LOGISTICS: ["logistics", "mobilization", "readiness-status"],
  LUK_INTAKE: ["mission-orders", "deployment-planning", "resource-requests"],
  OPERATIONS: ["mission-orders", "deployment-planning", "resource-requests"],
  READINESS: ["mobilization", "readiness-status", "personnel-directory"],
  REGIONAL: ["resource-requests", "logistics", "collaboration"],
  SITUATION: ["lage-board", "reporting", "safety-checks"],
};

export function defaultOperationsWorkspaceForPerspective(
  perspective: OperationalPerspectiveId,
): OperationsFlexWorkspaceId {
  return DEFAULT_WORKSPACE_BY_PERSPECTIVE[perspective];
}

export function operationsWorkspaceForPanel(
  panel: WorkspaceComponentId | null | undefined,
  perspective: OperationalPerspectiveId,
): OperationsFlexWorkspaceId {
  if (panel === null || panel === undefined) {
    return defaultOperationsWorkspaceForPerspective(perspective);
  }
  if (panel === "mobilization") {
    if (perspective === "LOGISTICS") return "LOGI";
    if (perspective === "READINESS") return "CAP";
    return "FIELD";
  }
  return WORKSPACE_BY_PANEL[panel] ?? defaultOperationsWorkspaceForPerspective(perspective);
}

export function operationalWorkspaceDefinition(
  id: OperationsFlexWorkspaceId,
): OperationalWorkspaceDefinition {
  return OPERATIONAL_WORKSPACES[id];
}

export function operationsWorkspacePanelGroups(
  id: OperationsFlexWorkspaceId,
  perspective: OperationalPerspectiveId,
  deviceMode: "desktop" | "tablet",
): Readonly<{
  primary: readonly WorkspaceComponentId[];
  support: readonly WorkspaceComponentId[];
}> {
  const definition = operationalWorkspaceDefinition(id);
  const candidateSupport = id === "CLOSE"
    ? CLOSE_SUPPORT_BY_PERSPECTIVE[perspective]
    : definition.supportComponents;
  const perspectiveValue = PERSPECTIVES[perspective];
  const legacyPlanningIsRelevant =
    perspectiveValue.primaryComponents.includes("deployment-planning") ||
    perspectiveValue.supportComponents.includes("deployment-planning") ||
    perspectiveValue.optionalComponents.includes("deployment-planning") ||
    perspectiveValue.mobileComponents.includes("deployment-planning");
  const legacyPlanningIsDefault =
    perspectiveValue.primaryComponents.includes("deployment-planning") ||
    perspectiveValue.supportComponents.includes("deployment-planning");
  const isRelevant = (component: WorkspaceComponentId): boolean =>
    (isCapabilityWorkspacePanel(component) && legacyPlanningIsDefault) ||
    (isSituationMapPanel(component) && (
      perspectiveValue.primaryComponents.includes("situation-map") ||
      perspectiveValue.supportComponents.includes("situation-map") ||
      perspectiveValue.optionalComponents.includes("situation-map") ||
      perspectiveValue.mobileComponents.includes("situation-map")
    )) ||
    (isDispatchAlertingPanel(component) && (
      perspectiveValue.primaryComponents.includes("alerting") ||
      perspectiveValue.supportComponents.includes("alerting") ||
      perspectiveValue.optionalComponents.includes("alerting") ||
      perspectiveValue.mobileComponents.includes("alerting")
    )) ||
    perspectiveValue.primaryComponents.includes(component) ||
    perspectiveValue.supportComponents.includes(component) ||
    perspectiveValue.optionalComponents.includes(component) ||
    perspectiveValue.mobileComponents.includes(component);
  const primary = definition.primaryComponents.filter(isRelevant);
  const support = candidateSupport.filter(isRelevant);
  // Tablet keeps the existing responsive workbench as one tab. Specialist
  // roles that reference planning without owning it keep that role-first
  // workbench on desktop as well. Five simultaneous decision panes belong to
  // planning roles at command-post width.
  if (
    id === "CAP" &&
    legacyPlanningIsRelevant &&
    (deviceMode === "tablet" || !legacyPlanningIsDefault)
  ) {
    return { primary: ["deployment-planning"], support: support.slice(0, 2) };
  }
  // SITU is not truncated on tablet. Its support panels are tabs of the object
  // register, not extra simultaneous panes, so keeping them costs no screen
  // area — and truncating would silently drop the role's situation report and
  // safety panels from the tablet default.
  if (deviceMode === "tablet" && id !== "SITU") {
    return { primary: primary.slice(0, 3), support: support.slice(0, 2) };
  }
  return { primary, support };
}

export function operationsWorkspaceComponentIsRelevant(
  perspective: OperationalPerspectiveId,
  workspaceId: OperationsFlexWorkspaceId,
  component: WorkspaceComponentId,
): boolean {
  const perspectiveDefinitionValue = PERSPECTIVES[perspective];
  if (perspectiveDefinitionValue.contextComponents.includes(component)) return true;
  const workspaceDefinition = operationalWorkspaceDefinition(workspaceId);
  const belongsToWorkspace = workspaceDefinition.primaryComponents.includes(component) ||
    workspaceDefinition.supportComponents.includes(component) ||
    workspaceDefinition.optionalComponents.includes(component);
  if (!belongsToWorkspace) return false;
  if (isCapabilityWorkspacePanel(component)) {
    return perspectiveDefinitionValue.primaryComponents.includes("deployment-planning") ||
      perspectiveDefinitionValue.supportComponents.includes("deployment-planning") ||
      perspectiveDefinitionValue.optionalComponents.includes("deployment-planning") ||
      perspectiveDefinitionValue.mobileComponents.includes("deployment-planning");
  }
  if (isSituationMapPanel(component)) {
    return perspectiveDefinitionValue.primaryComponents.includes("situation-map") ||
      perspectiveDefinitionValue.supportComponents.includes("situation-map") ||
      perspectiveDefinitionValue.optionalComponents.includes("situation-map") ||
      perspectiveDefinitionValue.mobileComponents.includes("situation-map");
  }
  if (isDispatchAlertingPanel(component)) {
    return perspectiveDefinitionValue.primaryComponents.includes("alerting") ||
      perspectiveDefinitionValue.supportComponents.includes("alerting") ||
      perspectiveDefinitionValue.optionalComponents.includes("alerting") ||
      perspectiveDefinitionValue.mobileComponents.includes("alerting");
  }
  return perspectiveDefinitionValue.primaryComponents.includes(component) ||
    perspectiveDefinitionValue.supportComponents.includes(component) ||
    perspectiveDefinitionValue.optionalComponents.includes(component) ||
    perspectiveDefinitionValue.mobileComponents.includes(component);
}

/**
 * Tablet starts with a deliberately smaller working set than desktop. Nothing
 * is made inaccessible: omitted, authorized panels remain in optional
 * discovery/deep links. The legacy order surface is intentionally optional,
 * not part of any normal desktop or tablet starting arrangement.
 */
export const TABLET_PERSPECTIVE_COMPONENTS: Readonly<Record<OperationalPerspectiveId, {
  readonly primary: readonly WorkspaceComponentId[];
  readonly support: readonly WorkspaceComponentId[];
}>> = {
  COMMAND: { primary: ["resource-requests", "deployment-planning", "mission-orders"], support: ["mobilization"] },
  LUK_INTAKE: { primary: ["alerting", "resource-requests"], support: ["deployment-planning"] },
  SITUATION: { primary: ["situation-map", "incident-reporting", "lage-board"], support: [] },
  ASSESSMENT: { primary: ["requirement-review", "safety-checks"], support: ["collaboration"] },
  OPERATIONS: { primary: ["resource-requests", "deployment-planning", "mission-orders"], support: ["mobilization"] },
  LOGISTICS: { primary: ["logistics", "resource-requests", "mobilization"], support: [] },
  READINESS: { primary: ["personnel-directory", "readiness-status", "mobilization"], support: [] },
  FIELD_LEADERSHIP: { primary: ["mission-orders", "field-tasks", "mobilization"], support: ["situation-map", "collaboration"] },
  FIELD_PARTICIPANT: { primary: ["field-tasks", "situation-map"], support: ["safety-checks", "collaboration"] },
  DOCUMENTATION: { primary: ["etb-log", "incident-reporting", "lage-board"], support: [] },
  COMMUNICATIONS: { primary: ["collaboration", "alerting"], support: [] },
  REGIONAL: { primary: ["resource-requests", "deployment-planning", "logistics"], support: [] },
};

function assignment(
  roleCode: string,
  perspective: PerspectiveId | null,
  operationsPerspective: OperationalPerspectiveId | null,
  defaultTarget: PerspectiveTarget | null,
  incidentAppointmentRole?: IncidentAppointmentRole,
): RolePerspectiveAssignment {
  const definition = perspective === null ? null : PERSPECTIVES[perspective];
  const operational = operationsPerspective !== null;
  return {
    roleCode,
    label: { de: humanizeRoleCode(roleCode), en: humanizeRoleCode(roleCode) },
    perspective,
    operationsPerspective,
    defaultTarget,
    requiresMissionRead: operationsPerspective !== null,
    requiredPermissions: operational ? ["mission_read"] : [],
    defaultComponents: definition === null
      ? []
      : [...definition.primaryComponents, ...definition.supportComponents],
    optionalComponents: definition?.optionalComponents ?? [],
    supportedDevices: perspective === "ADMIN"
      ? ["desktop", "tablet"]
      : perspective === null
        ? []
        : ["desktop", "tablet", "mobile"],
    capabilityMode: perspective === null ? "NONE" : "READ_WRITE",
    scope: {
      organization: "ACTIVE_GRANT",
      incident: operational ? "ACTIVE_APPOINTMENT_OR_GRANT" : "NONE",
      mission: operational ? "ACTIVE_GRANT" : "NONE",
    },
    expirationBehavior: "RECOMPUTE_ON_CONTEXT_REFRESH",
    ...(incidentAppointmentRole === undefined ? {} : { incidentAppointmentRole }),
  };
}

const role = (
  roleCode: string,
  perspective: PerspectiveId,
  operationsPerspective: OperationalPerspectiveId = perspective as OperationalPerspectiveId,
  defaultTarget: PerspectiveTarget = PERSPECTIVES[perspective].defaultTarget,
  incidentAppointmentRole?: IncidentAppointmentRole,
): RolePerspectiveAssignment => assignment(
  roleCode,
  perspective,
  operationsPerspective,
  defaultTarget,
  incidentAppointmentRole,
);

const noOperationalPerspective = (roleCode: string): RolePerspectiveAssignment =>
  assignment(roleCode, null, null, null);

const standalonePerspective = (
  roleCode: string,
  perspective: PerspectiveId,
  defaultTarget: PerspectiveTarget | null,
  requiredPermissions: readonly PermissionCode[],
): RolePerspectiveAssignment => ({
  ...assignment(roleCode, perspective, null, defaultTarget),
  requiredPermissions,
  requiresMissionRead: false,
  capabilityMode: "READ_WRITE",
});

const ADMIN_ROLES = [
  "access_administrator",
  "configuration_proposer",
  "configuration_approver",
  "personnel_data_steward",
  "qualification_steward",
  "reference_data_steward",
  "speech_configuration_administrator",
  "security_operator",
  "security_auditor",
  "superuser",
  "lv_admin",
  "rst_admin",
  "ov_admin",
  "master_data_admin",
] as const;

const ROLE_ASSIGNMENTS: readonly RolePerspectiveAssignment[] = [
  noOperationalPerspective("account_holder"),
  ...ADMIN_ROLES.map((roleCode) => standalonePerspective(
    roleCode,
    "ADMIN",
    target("/administration"),
    [],
  )),
  role("readiness_decision_maker", "READINESS"),
  role("mission_coordinator", "COMMAND"),
  // Lands on `/logistics` (its own route) rather than a second link into
  // `/operations`: the quartermaster's whole job is the equipment picture, and
  // an entry that shares `/operations`' path could never highlight in the
  // header. Assets is the opening panel because availability is the first
  // question this role answers.
  {
    ...role("schirrmeister", "LOGISTICS", "LOGISTICS", target("/logistics", "asset-directory", undefined, "LOGI")),
    // The quartermaster header carries the Logistics workspace and nothing
    // else: everything this role does (assets, readiness attestation,
    // mobilization condition, supply requests) is a tab inside it, so a second
    // header entry could only lead back to the same surface. Scoped to this
    // role, NOT to the LOGISTICS perspective, which `s4_logistics`,
    // `logistics_processor` and `request_processor` also use with their full
    // headers intact.
    headerItemIds: ["logistics"],
    headerWorkspaceEntryIds: ["assets-readiness", "logistics", "logistics-movement", "resource-requests"],
  },
  role("ortsbeauftragter", "COMMAND"),
  role("regionalstelle", "REGIONAL"),
  role("command_context_administrator", "COMMAND"),
  role("request_originator", "OPERATIONS", "OPERATIONS", target("/operations", "resource-requests", undefined, "REQ")),
  role("request_processor", "LOGISTICS", "LOGISTICS", target("/operations", "resource-requests", undefined, "REQ")),
  role("resource_allocator", "OPERATIONS", "OPERATIONS", target("/operations", "resource-requests", undefined, "REQ")),
  role("luk_activation_administrator", "LUK_INTAKE"),
  role("luk_operator", "LUK_INTAKE"),
  {
    ...role("order_issuer", "OPERATIONS", "OPERATIONS", target("/operations", "mission-orders", "compose", "ORD")),
    headerWorkspaceEntryIds: ["mission-orders-compose", "operations", "capabilities-resources", "situation", "operational-logbook"],
  },
  { ...role("order_recipient", "FIELD_LEADERSHIP", "FIELD_LEADERSHIP", target("/modules/field-operations?screen=order")), headerItemIds: ["field-operations"] },
  role("travel_authorizer", "OPERATIONS", "OPERATIONS", target("/operations", "mission-orders", "authorization", "ORD")),
  role("form_submitter", "DOCUMENTATION", "DOCUMENTATION", target("/operations", "incident-reporting", undefined, "ETB")),
  role("form_approver", "DOCUMENTATION", "DOCUMENTATION", target("/operations", "incident-reporting", undefined, "ETB")),
  role("logistics_processor", "LOGISTICS"),
  role("document_uploader", "DOCUMENTATION", "DOCUMENTATION", target("/operations", "incident-reporting", undefined, "ETB")),
  role("document_reviewer", "DOCUMENTATION", "DOCUMENTATION", target("/operations", "incident-reporting", undefined, "ETB")),
  role("report_viewer", "DOCUMENTATION", "DOCUMENTATION", target("/operations", "reporting", undefined, "ETB")),
  role("report_exporter", "DOCUMENTATION", "DOCUMENTATION", target("/operations", "reporting", undefined, "ETB")),
  standalonePerspective("notification_recipient", "COMMUNICATIONS", null, ["notification_read"]),
  standalonePerspective("collaboration_member", "COMMUNICATIONS", target("/modules/collaboration"), ["channel_read"]),
  standalonePerspective("collaboration_moderator", "COMMUNICATIONS", target("/modules/collaboration"), ["channel_read"]),
  role("etb_promotion_proposer", "DOCUMENTATION"),
  role("etb_reviewer", "DOCUMENTATION"),
  role("map_publisher", "SITUATION"),
  standalonePerspective("tactical_icon_publisher", "ADMIN", target("/administration?section=icons"), ["icon_catalog_read"]),
  standalonePerspective("records_officer", "ADMIN", target("/administration?section=governance"), ["retention_policy_manage"]),
  {
    ...role("registration_officer", "READINESS", "READINESS", target("/operations", "mobilization", undefined, "CAP")),
    headerWorkspaceEntryIds: ["force-registration", "mission-orders"],
  },
  { ...role("field_helfer", "FIELD_PARTICIPANT"), headerItemIds: ["field-operations"] },
  { ...role("zugfuehrer", "FIELD_LEADERSHIP"), headerItemIds: ["field-operations"] },
  role("luk_dispatcher", "LUK_INTAKE"),
  role("incident_commander", "COMMAND", "COMMAND", PERSPECTIVES.COMMAND.defaultTarget, "INCIDENT_COMMANDER"),
  {
    ...role("s1_personnel", "READINESS", "READINESS", target("/operations", "personnel-directory", undefined, "CAP"), "S1_PERSONNEL"),
    headerWorkspaceEntryIds: ["personnel-forces", "readiness", "force-registration", "operational-logbook", "collaboration", "incident-setup", "forms", "situation"],
  },
  role("s2_situation_reporting", "SITUATION", "SITUATION", PERSPECTIVES.SITUATION.defaultTarget, "S2_SITUATION_REPORTING"),
  {
    ...role("s3_operations", "OPERATIONS", "OPERATIONS", PERSPECTIVES.OPERATIONS.defaultTarget, "S3_OPERATIONS"),
    headerWorkspaceEntryIds: ["operations", "incident-setup", "mission-orders", "mobilization", "situation", "operational-logbook", "collaboration", "capabilities-resources", "logistics", "completion"],
  },
  role("s4_logistics", "LOGISTICS", "LOGISTICS", PERSPECTIVES.LOGISTICS.defaultTarget, "S4_LOGISTICS"),
  role("s6_communications", "COMMUNICATIONS", "COMMUNICATIONS", PERSPECTIVES.COMMUNICATIONS.defaultTarget, "S6_COMMUNICATIONS"),
  role("etb_keeper", "DOCUMENTATION", "DOCUMENTATION", PERSPECTIVES.DOCUMENTATION.defaultTarget, "ETB_KEEPER"),
  { ...role("unit_leader", "FIELD_LEADERSHIP"), headerItemIds: ["field-operations"] },
  { ...role("field_user", "FIELD_PARTICIPANT"), headerItemIds: ["field-operations"] },
  role("technical_adviser", "ASSESSMENT"),
  {
    ...role("operational_planner", "OPERATIONS"),
    headerWorkspaceEntryIds: ["operations", "capabilities-resources", "situation", "collaboration", "operational-logbook", "mission-orders", "logistics", "closure-reports"],
  },
  role("auditor", "DOCUMENTATION", "DOCUMENTATION", target("/operations", "reporting", undefined, "ETB")),
];

export const ROLE_PERSPECTIVE_ASSIGNMENTS: ReadonlyMap<string, RolePerspectiveAssignment> =
  new Map(ROLE_ASSIGNMENTS.map((value) => [value.roleCode, value]));

export const REGISTERED_ROLE_CODES: readonly string[] = Object.freeze(
  ROLE_ASSIGNMENTS.map((value) => value.roleCode),
);

const ROLE_ALIASES: Readonly<Record<string, string>> = {
  super_admin: "superuser",
  helfer: "field_helfer",
};

export function normalizeRoleCode(roleCode: string): string {
  const normalized = roleCode.trim().toLowerCase();
  return ROLE_ALIASES[normalized] ?? normalized;
}

export function rolePerspectiveAssignment(
  roleCode?: string | null,
): RolePerspectiveAssignment | null {
  if (roleCode === undefined || roleCode === null || roleCode.trim() === "") return null;
  return ROLE_PERSPECTIVE_ASSIGNMENTS.get(normalizeRoleCode(roleCode)) ?? null;
}

/**
 * Resolves a stable, duplicate-free set of presentation perspectives. The
 * active role is first (and therefore remains the default); the remaining
 * perspectives use the canonical registry order. Callers must still
 * permission-filter every target.
 */
export function rolePerspectiveAssignmentsForRoles(
  roleCodes: readonly string[],
  activeRoleCode?: string | null,
): readonly RolePerspectiveAssignment[] {
  const ordered = activeRoleCode === undefined || activeRoleCode === null
    ? roleCodes
    : [activeRoleCode, ...roleCodes];
  const seenPerspectives = new Set<PerspectiveId>();
  const resolved: RolePerspectiveAssignment[] = [];
  for (const roleCode of ordered) {
    const value = rolePerspectiveAssignment(roleCode);
    if (value?.perspective === null || value === null) continue;
    if (seenPerspectives.has(value.perspective)) continue;
    seenPerspectives.add(value.perspective);
    resolved.push(value);
  }
  const activePerspective = rolePerspectiveAssignment(activeRoleCode)?.perspective ?? null;
  return resolved.sort((left, right) => {
    if (left.perspective === activePerspective) return -1;
    if (right.perspective === activePerspective) return 1;
    return PERSPECTIVE_IDS.indexOf(left.perspective as PerspectiveId) -
      PERSPECTIVE_IDS.indexOf(right.perspective as PerspectiveId);
  });
}

export function perspectiveForRole(roleCode?: string | null): PerspectiveId | null {
  return rolePerspectiveAssignment(roleCode)?.perspective ?? null;
}

export function operationsPerspectiveForRole(
  roleCode?: string | null,
): OperationalPerspectiveId | null {
  return rolePerspectiveAssignment(roleCode)?.operationsPerspective ?? null;
}

export function perspectiveDefinition(id: PerspectiveId): PerspectiveDefinition {
  return PERSPECTIVES[id];
}

export function headerWorkspaceEntriesForRole(
  roleCode?: string | null,
): readonly HeaderWorkspaceEntryDefinition[] {
  const assignmentValue = rolePerspectiveAssignment(roleCode);
  if (assignmentValue?.perspective === null || assignmentValue === null) return [];
  const definition = perspectiveDefinition(assignmentValue.perspective);
  const ids = assignmentValue.headerWorkspaceEntryIds ?? definition.headerWorkspaceEntryIds;
  return ids
    .map((id) => HEADER_WORKSPACE_ENTRIES.get(id))
    .filter((value): value is HeaderWorkspaceEntryDefinition => value !== undefined);
}

export function defaultWorkspacePanelForRole(
  roleCode: string | null | undefined,
  workspaceId: OperationsFlexWorkspaceId,
): WorkspaceComponentId | null {
  const assignmentValue = rolePerspectiveAssignment(roleCode);
  if (assignmentValue?.operationsPerspective === null || assignmentValue === null) return null;
  const contextComponents = perspectiveDefinition(assignmentValue.operationsPerspective)
    .contextComponents;
  return headerWorkspaceEntriesForRole(roleCode)
    .find((entryValue) =>
      entryValue.workspaceId === workspaceId &&
      entryValue.target.component !== undefined &&
      !contextComponents.includes(entryValue.target.component))
    ?.target.component ?? null;
}

export function isOperationsFlexWorkspaceId(
  value: string | null | undefined,
): value is OperationsFlexWorkspaceId {
  return value !== null && value !== undefined &&
    value !== "HOME" && value !== "ADMIN" &&
    (OPERATIONAL_WORKSPACE_IDS as readonly string[]).includes(value);
}

export function perspectiveComponentIsRelevant(
  perspective: OperationalPerspectiveId,
  component: WorkspaceComponentId,
): boolean {
  const definition = PERSPECTIVES[perspective];
  const declared = (candidate: WorkspaceComponentId): boolean =>
    definition.contextComponents.includes(candidate) ||
    definition.primaryComponents.includes(candidate) ||
    definition.supportComponents.includes(candidate) ||
    definition.mobileComponents.includes(candidate) ||
    definition.optionalComponents.includes(candidate);
  // The register and the editor were split out of the map and are relevant
  // exactly where it is; the perspective lists the feature once, not three
  // times. Without this a restored SITU layout drops both panels on reload.
  if (isSituationMapPanel(component)) return declared("situation-map");
  return declared(component);
}

/** Permission fallback for genuinely unmapped future role codes. */
export function inferOperationsPerspective(
  canDiscover: (permissions: readonly PermissionCode[]) => boolean,
): OperationalPerspectiveId {
  if (canDiscover(["luk_work_manage"]) || canDiscover(["assistance_request_register"])) return "LUK_INTAKE";
  if (canDiscover(["channel_manage"])) return "COMMUNICATIONS";
  if (canDiscover(["logistics_request_process"]) || canDiscover(["logistics_delivery_manage"])) return "LOGISTICS";
  if (canDiscover(["resource_allocate"]) || canDiscover(["order_issue"])) return "OPERATIONS";
  if (canDiscover(["map_object_publish"]) || canDiscover(["situation_report_create"])) return "SITUATION";
  if (canDiscover(["etb_entry_append"]) || canDiscover(["report_export"])) return "DOCUMENTATION";
  if (canDiscover(["readiness_decide_manual"]) || canDiscover(["registration_approve"])) return "READINESS";
  return "COMMAND";
}
